import React, { useState } from 'react'
import useToast from 'hooks/useToast'
import {
  listDealDepositors,
  patchShortlistEntries,
  startShortlistRound,
} from 'submission/lib/shortlist-api'
import type { Depositor, PatchAction, ShortlistRound } from './types'

type Props = {
  open: boolean
  dealId: string
  defaultTargetCount: number | null
  defaultDeadlineHours: number | null
  onClose: () => void
  onStarted: (round: ShortlistRound) => void | Promise<void>
}

const buildSeedActions = (
  depositors: Depositor[],
  targetCount: number,
): PatchAction[] => {
  if (!Number.isFinite(targetCount) || targetCount <= 0) return []

  return [...depositors]
    .filter((depositor) => depositor.has_privy_account)
    .sort((a, b) => a.rank - b.rank)
    .slice(0, targetCount)
    .map((depositor) => ({
      privy_id: depositor.privy_id,
      action: 'manual_add',
      wallet_address: depositor.wallet_address,
      deposit_amount: depositor.deposit_amount,
      rank: depositor.rank,
    }))
}

const StartRoundModal: React.FC<Props> = ({
  open,
  dealId,
  defaultTargetCount,
  defaultDeadlineHours,
  onClose,
  onStarted,
}) => {
  const { showToast } = useToast()
  const [targetCount, setTargetCount] = useState('')
  const [deadlineHours, setDeadlineHours] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!open) return null

  const submit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const body: { target_count?: number; deadline_hours?: number } = {}
      if (targetCount !== '') body.target_count = Number(targetCount)
      if (deadlineHours !== '') body.deadline_hours = Number(deadlineHours)
      const round = await startShortlistRound(dealId, body)

      let seedError: Error | null = null
      let seededCount = 0

      try {
        const depositors = await listDealDepositors(dealId)
        const actions = buildSeedActions(depositors, round.target_count)
        if (actions.length > 0) {
          await patchShortlistEntries(dealId, round.id, actions)
          seededCount = actions.length
        }
      } catch (e: any) {
        seedError =
          e instanceof Error
            ? e
            : new Error(e?.message ?? 'Failed to seed depositors')
      }

      await onStarted(round)
      onClose()

      if (seedError) {
        showToast({
          type: 'error',
          message: `Round started, but auto-seeding failed: ${seedError.message}`,
        })
        return
      }

      showToast({
        type: 'success',
        message:
          seededCount > 0
            ? `Round started and seeded ${seededCount} candidate(s)`
            : 'Round started. No eligible depositors were seeded.',
      })
    } catch (e: any) {
      setError(e?.message ?? 'Failed to start round')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded p-6 w-[420px]">
        <h3 className="text-lg font-medium mb-4">Start a new round</h3>
        <label className="block mb-3 text-sm">
          <span className="block text-slate-600 mb-1">Target count</span>
          <input
            type="number"
            min={1}
            placeholder={
              defaultTargetCount != null ? String(defaultTargetCount) : ''
            }
            value={targetCount}
            onChange={(e) => setTargetCount(e.target.value)}
            className="border rounded px-2 py-1 w-full"
          />
        </label>
        <label className="block mb-3 text-sm">
          <span className="block text-slate-600 mb-1">Deadline hours</span>
          <input
            type="number"
            min={1}
            placeholder={
              defaultDeadlineHours != null ? String(defaultDeadlineHours) : ''
            }
            value={deadlineHours}
            onChange={(e) => setDeadlineHours(e.target.value)}
            className="border rounded px-2 py-1 w-full"
          />
        </label>
        {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            className="px-3 py-1 border rounded"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
            onClick={submit}
            disabled={submitting}
          >
            {submitting ? 'Starting…' : 'Start round'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default StartRoundModal
